import { z } from 'zod';
import {
  connectionTypeSchema,
  fiberPresenceSchema,
  packCodeSchema,
  projectNatureSchema,
  proPrestationSchema,
  projectStatusSchema,
  requestTypeSchema,
  typologySchema,
  unitTypeSchema,
} from './enums.js';

export const addressSchema = z.object({
  line1: z.string().trim().min(1).max(200),
  line2: z.string().trim().max(200).optional().nullable(),
  city: z.string().trim().min(1).max(120),
  postalCode: z.string().trim().regex(/^[0-9A-Z -]{3,12}$/i, 'Code postal invalide'),
  country: z.string().trim().min(2).max(80).default('France'),
});
export type Address = z.infer<typeof addressSchema>;

export const projectInfoSchema = z.object({
  address: addressSchema,
  contactName: z.string().trim().min(1).max(160),
  contactPhone: z.string().trim().min(6).max(20),
  desiredDelay: z.string().trim().min(1).max(120),
  buildingState: z.enum(['neuf', 'renovation']),
  buildingsCount: z.number().int().min(1).max(10_000),
  housingCount: z.number().int().min(0).max(100_000),
  housingPerBuilding: z.number().int().min(0).max(10_000),
  fiberInstallationsCount: z.number().int().min(0).max(100_000),
  proLocalsCount: z.number().int().min(0).max(100_000),
  // Segment professionnels & collectivités (optionnels, rétro-compatibles).
  projectNature: projectNatureSchema.optional(),
  organisation: z.string().trim().max(200).optional(),
  contactFunction: z.string().trim().max(120).optional(),
  prestations: proPrestationSchema.array().optional(),
});
export type ProjectInfo = z.infer<typeof projectInfoSchema>;

export const unitTypologySchema = z.object({
  unitType: unitTypeSchema,
  count: z.number().int().min(0).max(100_000),
});
export type UnitTypology = z.infer<typeof unitTypologySchema>;

export const fiberQuestionsSchema = z.object({
  fiberKnownAtAddress: z.boolean(),
  fiberAlreadyInstalled: z.boolean(),
  hasFourreaux: z.boolean(),
  hasTelecomChamber: z.boolean(),
  connectionType: connectionTypeSchema,
  civilWorksDone: z.boolean(),
  // « La fibre est-elle présente devant la propriété ? » (tri-état, optionnel).
  fiberInFrontOfProperty: fiberPresenceSchema.optional(),
});
export type FiberQuestions = z.infer<typeof fiberQuestionsSchema>;

export const qualificationAnswersSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]),
);
export type QualificationAnswers = z.infer<typeof qualificationAnswersSchema>;

/**
 * Funnel pack-first : a la creation, le client a juste choisi un pack (ou pas
 * encore). Le requestType reel et la typology sont fixes a l'etape de
 * qualification. On accepte des defauts pour rester retro-compatible avec les
 * appels existants qui posent requestType + typology a la creation.
 */
export const createProjectSchema = z.object({
  requestType: requestTypeSchema.default('etude_raccordement'),
  typology: typologySchema.default('maison_individuelle'),
});
export type CreateProjectInput = z.infer<typeof createProjectSchema>;

export const updateProjectSchema = z.object({
  requestType: requestTypeSchema.optional(),
  typology: typologySchema.optional(),
  qualificationAnswers: qualificationAnswersSchema.optional(),
  info: projectInfoSchema.optional(),
  unitTypologies: z.array(unitTypologySchema).optional(),
  fiberQuestions: fiberQuestionsSchema.optional(),
  packCode: packCodeSchema.optional(),
});
export type UpdateProjectInput = z.infer<typeof updateProjectSchema>;

export const projectSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  reference: z.string(),
  requestType: requestTypeSchema,
  typology: typologySchema.nullable(),
  qualificationAnswers: qualificationAnswersSchema.nullable(),
  info: projectInfoSchema.nullable(),
  unitTypologies: z.array(unitTypologySchema),
  fiberQuestions: fiberQuestionsSchema.nullable(),
  packCode: packCodeSchema.nullable(),
  status: projectStatusSchema,
  assignedTechnicianId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Project = z.infer<typeof projectSchema>;

